/**
 * Input Validation Service
 * 
 * WASA Compliance: Comprehensive input validation and sanitization
 * Following OWASP guidelines for secure input handling
 */

/**
 * Sanitize string input to prevent XSS attacks
 * Removes potentially dangerous HTML/script tags
 */
export const sanitizeString = (input) => {
    if (typeof input !== 'string') {
        return input;
    }
    
    // Remove HTML tags
    let sanitized = input.replace(/<[^>]*>/g, '');
    
    // Escape special characters
    sanitized = sanitized
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#x27;')
        .replace(/\//g, '&#x2F;');
    
    // Remove null bytes
    sanitized = sanitized.replace(/\0/g, '');
    
    // Trim whitespace
    sanitized = sanitized.trim();
    
    return sanitized;
};

/**
 * SECURITY: Validate and sanitize email address
 * Standard email regex validation
 * Prevents: Script injection, HTML injection, XSS attacks
 */
export const validateAndSanitizeEmail = (email) => {
    if (!email || typeof email !== 'string') {
        return { isValid: false, sanitized: '', error: 'Email is required' };
    }
    
    // Trim and convert to lowercase
    let sanitized = email.trim().toLowerCase();
    
    // Remove null bytes and control characters
    sanitized = sanitized.replace(/\0/g, '').replace(/[\x00-\x1F\x7F]/g, '');
    
    // SECURITY: Standard email regex - prevents script/HTML injection
    // RFC 5322 compliant email regex (simplified)
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    
    if (!emailRegex.test(sanitized)) {
        return { isValid: false, sanitized: '', error: 'Invalid email format' };
    }
    
    // Additional check: prevent script tags and HTML
    if (/<[^>]*>|script|javascript:/i.test(sanitized)) {
        return { isValid: false, sanitized: '', error: 'Invalid email format' };
    }
    
    // Length validation (RFC 5322: max 320 characters)
    if (sanitized.length > 320) {
        return { isValid: false, sanitized: '', error: 'Email is too long (max 320 characters)' };
    }
    
    if (sanitized.length < 5) {
        return { isValid: false, sanitized: '', error: 'Email is too short' };
    }
    
    return { isValid: true, sanitized, error: null };
};

/**
 * SECURITY: Validate and sanitize phone number
 * Regex: ^[0-9]{10}$
 * Allows: Numbers only, exactly 10 digits
 * Prevents: Injection attacks, malformed phone numbers
 */
export const validateAndSanitizeMobile = (mobile) => {
    if (!mobile) {
        return { isValid: false, sanitized: '', error: 'Phone number is required' };
    }
    
    // Convert to string and remove all non-digit characters
    const sanitized = String(mobile).replace(/\D/g, '');
    
    // SECURITY: Phone number regex - exactly 10 digits
    // Regex: ^[0-9]{10}$
    const phoneRegex = /^[0-9]{10}$/;
    
    if (!phoneRegex.test(sanitized)) {
        return { isValid: false, sanitized: '', error: 'Phone number must be exactly 10 digits' };
    }
    
    return { isValid: true, sanitized, error: null };
};

/**
 * True when password contains adjacent digits that differ by 1 (e.g. "12", "43").
 * Same rule used on registration.
 */
export const hasConsecutiveNumberSequence = (str) => {
    if (!str || typeof str !== 'string') return false;
    for (let i = 1; i < str.length; i++) {
        const prev = parseInt(str[i - 1], 10);
        const curr = parseInt(str[i], 10);
        if (!Number.isNaN(prev) && !Number.isNaN(curr)) {
            if (Math.abs(prev - curr) === 1) {
                return true;
            }
        }
    }
    return false;
};

/**
 * UI checklist rules — keep in sync with validatePassword().
 * Used by registration suggestions and reset-password checklist.
 */
export const PASSWORD_REQUIREMENT_RULES = [
    {
        id: 'length',
        label: 'Minimum 8 characters',
        test: (p) => !!p && p.length >= 8,
    },
    {
        id: 'lower',
        label: 'At least one lowercase letter',
        test: (p) => /[a-z]/.test(p || ''),
    },
    {
        id: 'upper',
        label: 'At least one uppercase letter',
        test: (p) => /[A-Z]/.test(p || ''),
    },
    {
        id: 'digit',
        label: 'At least one numeric digit',
        test: (p) => /\d/.test(p || ''),
    },
    {
        id: 'special',
        label: 'At least one special character (e.g. !@#$%)',
        test: (p) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(p || ''),
    },
    {
        id: 'consecutive',
        label: 'No consecutive numbers (e.g. 12, 43)',
        test: (p) => !!p && !hasConsecutiveNumberSequence(p),
    },
];

/**
 * Validate and sanitize password
 * WASA: Strong password requirements (shared by registration + password reset)
 */
export const validatePassword = (password) => {
    if (!password) {
        return { isValid: false, error: 'Password is required' };
    }
    
    if (typeof password !== 'string') {
        return { isValid: false, error: 'Password must be a string' };
    }
    
    // Length validation
    if (password.length < 8) {
        return { isValid: false, error: 'Password must be at least 8 characters long' };
    }
    
    if (password.length > 128) {
        return { isValid: false, error: 'Password is too long (max 128 characters)' };
    }
    
    // Check for at least one uppercase letter
    if (!/[A-Z]/.test(password)) {
        return { isValid: false, error: 'Password must contain at least one uppercase letter' };
    }
    
    // Check for at least one lowercase letter
    if (!/[a-z]/.test(password)) {
        return { isValid: false, error: 'Password must contain at least one lowercase letter' };
    }
    
    // Check for at least one number
    if (!/\d/.test(password)) {
        return { isValid: false, error: 'Password must contain at least one number' };
    }
    
    // Check for at least one special character
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
        return { isValid: false, error: 'Password must contain at least one special character' };
    }
    
    // Check for common weak passwords
    const commonPasswords = ['password', '12345678', 'qwerty', 'admin', 'welcome'];
    if (commonPasswords.some(common => password.toLowerCase().includes(common))) {
        return { isValid: false, error: 'Password is too common. Please choose a stronger password' };
    }
    
    // Check for consecutive identical characters (e.g., aaa, 111)
    if (/(.)\1{2,}/.test(password)) {
        return { isValid: false, error: 'Password cannot contain more than 2 consecutive identical characters' };
    }

    // Same as registration: reject consecutive number sequences like "12" or "43"
    if (hasConsecutiveNumberSequence(password)) {
        return {
            isValid: false,
            error: "Password cannot have consecutive number sequences like '12' or '43'.",
        };
    }
    
    return { isValid: true, error: null };
};

/**
 * Validate and sanitize numeric input
 */
export const validateAndSanitizeNumber = (value, options = {}) => {
    const { min, max, integer = false, required = true } = options;
    
    if (required && (value === null || value === undefined || value === '')) {
        return { isValid: false, sanitized: null, error: 'This field is required' };
    }
    
    if (!required && (value === null || value === undefined || value === '')) {
        return { isValid: true, sanitized: null, error: null };
    }
    
    // Convert to number
    const num = Number(value);
    
    if (isNaN(num)) {
        return { isValid: false, sanitized: null, error: 'Must be a valid number' };
    }
    
    // Check if integer is required
    if (integer && !Number.isInteger(num)) {
        return { isValid: false, sanitized: null, error: 'Must be an integer' };
    }
    
    // Range validation
    if (min !== undefined && num < min) {
        return { isValid: false, sanitized: num, error: `Value must be at least ${min}` };
    }
    
    if (max !== undefined && num > max) {
        return { isValid: false, sanitized: num, error: `Value must be at most ${max}` };
    }
    
    return { isValid: true, sanitized: num, error: null };
};

/**
 * SECURITY: Validate and sanitize text input (Address, City, State fields)
 * Regex: ^[a-zA-Z0-9 ,.]+$
 * Allows: Alphabets, numbers, space, comma, dot
 * Disallows: Special characters (*, <, >, { }, [ ], %, $, @, !, etc.)
 * Prevents: XSS, injection attacks, malformed payloads
 */
const prefixFieldError = (error, fieldLabel) =>
    fieldLabel ? `${fieldLabel}: ${error}` : error;

const DEFAULT_INVALID_CHAR_ERROR = 'Invalid characters. Only letters, numbers, spaces, commas, and dots are allowed';

export const validateAndSanitizeText = (text, options = {}) => {
    const {
        minLength = 1,
        maxLength = 1000,
        required = true,
        allowSpecialChars = false, // Default to strict validation
        pattern = null,
        fieldLabel = null, // Optional: prepend to error for field-specific messages
        invalidPatternError = null // Optional: custom message when pattern fails (e.g. for hyphens-only fields)
    } = options;

    if (required && (!text || text.trim() === '')) {
        const err = 'This field is required';
        return { isValid: false, sanitized: '', error: prefixFieldError(err, fieldLabel) };
    }

    if (!required && (!text || text.trim() === '')) {
        return { isValid: true, sanitized: '', error: null };
    }

    // Convert to string and trim
    let sanitized = String(text).trim();

    // SECURITY: Apply strict regex pattern for text fields (Address, City, State)
    // Pattern: ^[a-zA-Z0-9 ,.]+$ - Only allows alphanumeric, space, comma, dot
    const textFieldRegex = /^[a-zA-Z0-9 ,.]+$/;

    // Use provided pattern or default strict pattern
    const validationPattern = pattern || (allowSpecialChars ? null : textFieldRegex);

    if (validationPattern && !validationPattern.test(sanitized)) {
        const err = invalidPatternError || DEFAULT_INVALID_CHAR_ERROR;
        return {
            isValid: false,
            sanitized: '',
            error: prefixFieldError(err, fieldLabel)
        };
    }

    // Remove null bytes and control characters
    sanitized = sanitized.replace(/\0/g, '').replace(/[\x00-\x1F\x7F]/g, '');

    // Length validation
    if (sanitized.length < minLength) {
        const err = `Must be at least ${minLength} characters`;
        return { isValid: false, sanitized, error: prefixFieldError(err, fieldLabel) };
    }

    if (sanitized.length > maxLength) {
        const err = `Must be at most ${maxLength} characters`;
        return { isValid: false, sanitized, error: prefixFieldError(err, fieldLabel) };
    }

    return { isValid: true, sanitized, error: null };
};

/**
 * SECURITY: Validate and sanitize name fields
 * Regex: ^[a-zA-Z ]+$
 * Allows: Alphabets and spaces only
 * Prevents: Injection attacks, special characters in names
 */
export const validateAndSanitizeName = (name, options = {}) => {
    const {
        minLength = 1,
        maxLength = 100,
        required = true,
        fieldLabel = null
    } = options;

    if (required && (!name || name.trim() === '')) {
        const err = 'This field is required';
        return { isValid: false, sanitized: '', error: prefixFieldError(err, fieldLabel) };
    }

    if (!required && (!name || name.trim() === '')) {
        return { isValid: true, sanitized: '', error: null };
    }

    // Convert to string and trim
    let sanitized = String(name).trim();

    // SECURITY: Name field regex - only alphabets and spaces
    // Regex: ^[a-zA-Z ]+$
    const nameRegex = /^[a-zA-Z ]+$/;

    if (!nameRegex.test(sanitized)) {
        const err = 'Invalid characters. Only letters and spaces are allowed';
        return {
            isValid: false,
            sanitized: '',
            error: prefixFieldError(err, fieldLabel)
        };
    }

    // Remove null bytes and control characters
    sanitized = sanitized.replace(/\0/g, '').replace(/[\x00-\x1F\x7F]/g, '');

    // Length validation
    if (sanitized.length < minLength) {
        const err = `Must be at least ${minLength} characters`;
        return { isValid: false, sanitized, error: prefixFieldError(err, fieldLabel) };
    }

    if (sanitized.length > maxLength) {
        const err = `Must be at most ${maxLength} characters`;
        return { isValid: false, sanitized, error: prefixFieldError(err, fieldLabel) };
    }

    return { isValid: true, sanitized, error: null };
};

/**
 * Validate and sanitize date input
 */
export const validateAndSanitizeDate = (date, options = {}) => {
    const { minDate, maxDate, required = true } = options;
    
    if (required && !date) {
        return { isValid: false, sanitized: null, error: 'Date is required' };
    }
    
    if (!required && !date) {
        return { isValid: true, sanitized: null, error: null };
    }
    
    const dateObj = new Date(date);
    
    if (isNaN(dateObj.getTime())) {
        return { isValid: false, sanitized: null, error: 'Invalid date format' };
    }
    
    // Range validation
    if (minDate) {
        const min = new Date(minDate);
        if (dateObj < min) {
            return { isValid: false, sanitized: dateObj, error: `Date must be after ${minDate}` };
        }
    }
    
    if (maxDate) {
        const max = new Date(maxDate);
        if (dateObj > max) {
            return { isValid: false, sanitized: dateObj, error: `Date must be before ${maxDate}` };
        }
    }
    
    return { isValid: true, sanitized: dateObj, error: null };
};

/**
 * Validate and sanitize URL
 */
export const validateAndSanitizeURL = (url) => {
    if (!url || typeof url !== 'string') {
        return { isValid: false, sanitized: '', error: 'URL is required' };
    }
    
    const sanitized = sanitizeString(url);
    
    try {
        const urlObj = new URL(sanitized);
        
        // Only allow http and https protocols
        if (!['http:', 'https:'].includes(urlObj.protocol)) {
            return { isValid: false, sanitized, error: 'Only http and https URLs are allowed' };
        }
        
        return { isValid: true, sanitized, error: null };
    } catch (e) {
        return { isValid: false, sanitized, error: 'Invalid URL format' };
    }
};

/**
 * Validate and sanitize file name
 * Prevents path traversal attacks
 */
export const validateAndSanitizeFileName = (fileName) => {
    if (!fileName || typeof fileName !== 'string') {
        return { isValid: false, sanitized: '', error: 'File name is required' };
    }
    
    // Remove path traversal attempts
    let sanitized = fileName.replace(/\.\./g, '');
    sanitized = sanitized.replace(/[\/\\]/g, '');
    
    // Remove null bytes
    sanitized = sanitized.replace(/\0/g, '');
    
    // Remove control characters
    sanitized = sanitized.replace(/[\x00-\x1F\x7F]/g, '');
    
    // Length validation
    if (sanitized.length > 255) {
        return { isValid: false, sanitized, error: 'File name is too long (max 255 characters)' };
    }
    
    if (sanitized.length === 0) {
        return { isValid: false, sanitized, error: 'File name cannot be empty' };
    }
    
    return { isValid: true, sanitized, error: null };
};

/**
 * SECURITY: Validate and sanitize pincode/ZIP
 * Regex: ^[0-9]{6}$
 * Allows: Numbers only, exactly 6 digits
 * Prevents: Injection attacks, malformed input
 */
export const validateAndSanitizePincode = (pincode) => {
    if (!pincode) {
        return { isValid: false, sanitized: '', error: 'Pincode is required' };
    }
    
    // Remove all non-digit characters
    const sanitized = String(pincode).replace(/\D/g, '');
    
    // SECURITY: Pincode regex - exactly 6 digits
    // Regex: ^[0-9]{6}$
    const pincodeRegex = /^[0-9]{6}$/;
    
    if (!pincodeRegex.test(sanitized)) {
        return { isValid: false, sanitized: '', error: 'Pincode must be exactly 6 digits' };
    }
    
    return { isValid: true, sanitized, error: null };
};

/**
 * Validate Aadhaar number (12 digits)
 */
export const validateAndSanitizeAadhaar = (aadhaar) => {
    if (!aadhaar) {
        return { isValid: false, sanitized: '', error: 'Aadhaar number is required' };
    }
    
    const sanitized = String(aadhaar).replace(/\D/g, '');
    
    if (sanitized.length !== 12) {
        return { isValid: false, sanitized, error: 'Aadhaar number must be 12 digits' };
    }
    
    // Aadhaar should not start with 0 or 1
    if (/^[01]/.test(sanitized)) {
        return { isValid: false, sanitized, error: 'Invalid Aadhaar number format' };
    }
    
    return { isValid: true, sanitized, error: null };
};

/**
 * Validate registration number (alphanumeric with specific format)
 */
export const validateAndSanitizeRegistrationNumber = (regNumber) => {
    if (!regNumber) {
        return { isValid: false, sanitized: '', error: 'Registration number is required' };
    }
    
    // Sanitize - allow only alphanumeric and hyphens
    let sanitized = String(regNumber).replace(/[^a-zA-Z0-9\-]/g, '');
    
    if (sanitized.length < 3) {
        return { isValid: false, sanitized, error: 'Registration number is too short' };
    }
    
    if (sanitized.length > 50) {
        return { isValid: false, sanitized, error: 'Registration number is too long' };
    }
    
    return { isValid: true, sanitized, error: null };
};

/**
 * Validate year (4 digits, reasonable range)
 */
export const validateAndSanitizeYear = (year) => {
    const currentYear = new Date().getFullYear();
    const minYear = 1900;
    const maxYear = currentYear + 10; // Allow future years up to 10 years
    
    const result = validateAndSanitizeNumber(year, { 
        min: minYear, 
        max: maxYear, 
        integer: true 
    });
    
    if (!result.isValid) {
        return result;
    }
    
    // Ensure it's 4 digits
    if (String(result.sanitized).length !== 4) {
        return { isValid: false, sanitized: result.sanitized, error: 'Year must be 4 digits' };
    }
    
    return result;
};

/**
 * SECURITY: Validate and sanitize address field
 * Allows: Alphabets, numbers, spaces, and common address punctuation
 *   (comma, dot, slash, hyphen, hash, parentheses, colon, apostrophe, ampersand, plus)
 * Disallows: XSS / injection meta characters (*, <, >, { }, [ ], %, $, @, !, etc.)
 * Prevents: XSS, injection attacks, malformed payloads
 */
export const validateAndSanitizeAddress = (address, options = {}) => {
    const { fieldLabel = null } = options;

    if (!address) {
        const err = 'Address is required';
        return { isValid: false, sanitized: '', error: prefixFieldError(err, fieldLabel) };
    }

    // Convert to string; normalize KYC newlines/tabs to spaces before validation
    let sanitized = String(address)
        .replace(/[\r\n\t]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    // SECURITY: Address field regex - alphanumeric + common address punctuation
    const addressRegex = /^[a-zA-Z0-9 ,./#\-()':&+]+$/;

    if (!addressRegex.test(sanitized)) {
        const err = "Invalid characters. Only letters, numbers, spaces, and common address punctuation (,. / - # ( ) : ' & +) are allowed";
        return {
            isValid: false,
            sanitized: '',
            error: prefixFieldError(err, fieldLabel)
        };
    }

    // Remove null bytes and remaining control characters
    sanitized = sanitized.replace(/\0/g, '').replace(/[\x00-\x1F\x7F]/g, '');

    if (sanitized.length < 10) {
        const err = 'Address must be at least 10 characters';
        return { isValid: false, sanitized: '', error: prefixFieldError(err, fieldLabel) };
    }

    if (sanitized.length > 500) {
        const err = 'Address is too long (max 500 characters)';
        return { isValid: false, sanitized: '', error: prefixFieldError(err, fieldLabel) };
    }

    return { isValid: true, sanitized, error: null };
};

/**
 * Validate array input (for multi-select fields)
 */
export const validateArray = (arr, options = {}) => {
    const { minLength = 1, maxLength = 100, required = true } = options;
    
    if (required && (!Array.isArray(arr) || arr.length === 0)) {
        return { isValid: false, sanitized: [], error: 'At least one selection is required' };
    }
    
    if (!required && (!Array.isArray(arr) || arr.length === 0)) {
        return { isValid: true, sanitized: [], error: null };
    }
    
    if (arr.length < minLength) {
        return { isValid: false, sanitized: arr, error: `At least ${minLength} selection(s) required` };
    }
    
    if (arr.length > maxLength) {
        return { isValid: false, sanitized: arr, error: `Maximum ${maxLength} selections allowed` };
    }
    
    // Sanitize each element
    const sanitized = arr.map(item => {
        if (typeof item === 'string') {
            return sanitizeString(item);
        }
        return item;
    });
    
    return { isValid: true, sanitized, error: null };
};

/**
 * Comprehensive input validator - validates multiple fields at once
 */
export const validateFormInputs = (inputs, schema) => {
    const errors = {};
    const sanitized = {};
    
    for (const [fieldName, value] of Object.entries(inputs)) {
        const fieldSchema = schema[fieldName];
        
        if (!fieldSchema) {
            // Field not in schema, skip validation
            sanitized[fieldName] = value;
            continue;
        }
        
        const { type, ...options } = fieldSchema;
        let result;
        
        switch (type) {
            case 'email':
                result = validateAndSanitizeEmail(value);
                break;
            case 'mobile':
                result = validateAndSanitizeMobile(value);
                break;
            case 'password':
                result = validatePassword(value);
                break;
            case 'number':
                result = validateAndSanitizeNumber(value, options);
                break;
            case 'text':
                result = validateAndSanitizeText(value, options);
                break;
            case 'name':
                result = validateAndSanitizeName(value, options);
                break;
            case 'date':
                result = validateAndSanitizeDate(value, options);
                break;
            case 'pincode':
                result = validateAndSanitizePincode(value);
                break;
            case 'aadhaar':
                result = validateAndSanitizeAadhaar(value);
                break;
            case 'year':
                result = validateAndSanitizeYear(value);
                break;
            case 'address':
                result = validateAndSanitizeAddress(value);
                break;
            case 'array':
                result = validateArray(value, options);
                break;
            case 'registrationNumber':
                result = validateAndSanitizeRegistrationNumber(value);
                break;
            default:
                // Default to text sanitization
                result = validateAndSanitizeText(value, options);
        }
        
        if (!result.isValid) {
            errors[fieldName] = result.error;
        }
        
        sanitized[fieldName] = result.sanitized !== undefined ? result.sanitized : value;
    }
    
    return {
        isValid: Object.keys(errors).length === 0,
        errors,
        sanitized
    };
};
